import { socketService } from './socket';

export type Location = {
  latitude: number;
  longitude: number;
  accuracy?: number;
  timestamp: number;
  speed?: number | null;
  heading?: number | null;
};

type GeofenceArea = {
  id: string;
  name: string;
  type?: string;
  center: Location;
  radius: number; // meters
};

type LocationError = {
  code: number;
  message: string;
};

type NavigationRoute = {
  distance: number;
  duration: number;
  steps: {
    instruction: string;
    distance: number;
    duration: number;
    startLocation: Location;
    endLocation: Location;
  }[];
  polyline?: string;
};

export class LocationService {
  private watchId: number | null = null;
  private lastKnownLocation: Location | null = null;
  private isTracking = false;
  private updateInterval: ReturnType<typeof setInterval> | null = null;
  private readonly UPDATE_INTERVAL = 10000; // 10 seconds
  private readonly MIN_DISTANCE = 10; // 10 meters
  private geofences: GeofenceArea[] = [];
  private locationHistory: Location[] = [];
  private readonly MAX_HISTORY_SIZE = 100;

  constructor() {
    this.setupLocationTracking();
  }

  /** Request location permissions */
  async requestPermissions(): Promise<boolean> {
    if (!navigator.geolocation) {
      console.error("Geolocation not supported by this browser");
      return false;
    }

    try {
      await this.getCurrentPosition();
      return true;
    } catch (error) {
      console.error("Location permission denied:", error);
      return false;
    }
  }

  /** Get current position once */
  async getCurrentPosition(): Promise<Location> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        return reject(new Error("Geolocation not supported"));
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location: Location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: position.timestamp,
            speed: position.coords.speed ?? null,
            heading: position.coords.heading ?? null,
          };
          this.lastKnownLocation = location;
          resolve(location);
        },
        (error) => reject(this.mapGeolocationError(error)),
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 60000,
        }
      );
    });
  }

  /** Start continuous location tracking */
  async startTracking(): Promise<void> {
    if (this.isTracking) {
      console.log("Location tracking already active");
      return;
    }

    const hasPermission = await this.requestPermissions();
    if (!hasPermission) throw new Error("Location permission denied");

    this.isTracking = true;
    console.log("🌍 Starting location tracking...");

    this.watchId = navigator.geolocation.watchPosition(
      (position) => {
        this.handleLocationUpdate({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp,
          speed: position.coords.speed ?? null,
          heading: position.coords.heading ?? null,
        });
      },
      (error) => console.error("Location watch error:", this.mapGeolocationError(error)),
      { enableHighAccuracy: false, timeout: 20000, maximumAge: 60000 }
    );

    this.updateInterval = setInterval(() => {
      if (this.lastKnownLocation) {
        this.sendLocationUpdate(this.lastKnownLocation);
      }
    }, this.UPDATE_INTERVAL);

    const initialPosition = await this.getCurrentPosition();
    await this.sendLocationUpdate(initialPosition);
  }

  /** Stop tracking */
  stopTracking(): void {
    if (!this.isTracking) {
      console.log("Location tracking not active");
      return;
    }

    console.log("⏹️  Stopping location tracking...");
    this.isTracking = false;

    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }

    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
  }

  /** Handle location updates */
  private handleLocationUpdate(newLocation: Location): void {
    const previous = this.lastKnownLocation;

    if (previous && this.calculateDistance(previous, newLocation) < this.MIN_DISTANCE) return;

    this.addToLocationHistory(newLocation);
    this.checkGeofences(newLocation);
    this.lastKnownLocation = newLocation;

    socketService.updateLocation(newLocation.latitude, newLocation.longitude);
  }

  /** Add location to history */
  private addToLocationHistory(location: Location): void {
    this.locationHistory.push(location);
    if (this.locationHistory.length > this.MAX_HISTORY_SIZE) this.locationHistory.shift();
  }

  /** Check geofences */
  private checkGeofences(currentLocation: Location): void {
    for (const geofence of this.geofences) {
      const distance = this.calculateDistance(currentLocation, geofence.center);
      const isInside = distance <= geofence.radius;
      if (isInside) {
        socketService.emitToServer("geofence:entered", {
          geofenceId: geofence.id,
          geofenceName: geofence.name,
          type: geofence.type,
          location: currentLocation,
        });
      }
    }
  }

  /** Send location update (to server or socket) */
  private async sendLocationUpdate(location: Location): Promise<void> {
    try {
      socketService.updateLocation(location.latitude, location.longitude);
    } catch (error) {
      console.error("Failed to send location update:", error);
    }
  }

  /** Calculate distance (meters) */
  public calculateDistance(loc1: Location, loc2: Location): number {
    const R = 6371e3;
    const φ1 = (loc1.latitude * Math.PI) / 180;
    const φ2 = (loc2.latitude * Math.PI) / 180;
    const Δφ = ((loc2.latitude - loc1.latitude) * Math.PI) / 180;
    const Δλ = ((loc2.longitude - loc1.longitude) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) ** 2 +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  /** Error mapping */
  private mapGeolocationError(error: GeolocationPositionError): LocationError {
    const messages: Record<number, string> = {
      [error.PERMISSION_DENIED]: "Location access denied by user",
      [error.POSITION_UNAVAILABLE]: "Location unavailable",
      [error.TIMEOUT]: "Location request timed out",
    };
    return { code: error.code, message: messages[error.code] || "Unknown location error" };
  }

  /** Socket setup */
  private setupLocationTracking(): void {
    socketService.on("location:update_required", async () => {
      console.log("📍 Server requested location update");
      if (this.lastKnownLocation) {
        this.sendLocationUpdate(this.lastKnownLocation);
      } else {
        const loc = await this.getCurrentPosition();
        this.sendLocationUpdate(loc);
      }
    });
  }

  // ---- Public Utilities ----

  getLastKnownLocation() { return this.lastKnownLocation; }

  isCurrentlyTracking() { return this.isTracking; }

  getTrackingStatus() {
    return {
      isTracking: this.isTracking,
      lastLocation: this.lastKnownLocation,
      lastUpdate: this.lastKnownLocation?.timestamp || null,
    };
  }

  getLocationHistory() { return [...this.locationHistory]; }

  clearLocationHistory() { this.locationHistory = []; }

  addGeofence(geofence: GeofenceArea) { this.geofences.push(geofence); }

  removeGeofence(id: string) { this.geofences = this.geofences.filter(g => g.id !== id); }

  isWithinArea(loc: Location, center: Location, radius: number) {
    return this.calculateDistance(loc, center) <= radius;
  }

  getBearing(from: Location, to: Location) {
    const lat1 = (from.latitude * Math.PI) / 180;
    const lat2 = (to.latitude * Math.PI) / 180;
    const dLon = ((to.longitude - from.longitude) * Math.PI) / 180;
    const y = Math.sin(dLon) * Math.cos(lat2);
    const x =
      Math.cos(lat1) * Math.sin(lat2) -
      Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
    return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
  }
}
